import React, { useEffect, useLayoutEffect, useRef } from 'react'

// React owns the decoration; the global engine only changes its geometry/state.
// Resolve the global at effect time because me-buttons loads before it.
export function useHoverGlide(container, itemSelector, enabled = true) {
  const pill = useRef(null)
  const attached = useRef(null)
  // React can replace className when a menu opens or a tree enters author mode.
  useLayoutEffect(() => {
    const host = container.current
    if (
      attached.current &&
      host &&
      !host.classList.contains('me-hover-glide-ready')
    ) {
      host.classList.add('me-hover-glide-ready')
      attached.current.refresh()
    }
  })
  useEffect(() => {
    if (!enabled || !container.current || !pill.current) return
    const engine = window.MEHoverGlide
    if (!engine) return
    const attachment = engine.attach(container.current, {
      itemSelector,
      pill: pill.current,
    })
    attached.current = attachment
    return () => {
      attachment?.destroy()
      attached.current = null
    }
  }, [container, itemSelector, enabled])
  return pill
}

export const HoverGlidePill = React.forwardRef(function HoverGlidePill(
  { as: Tag = 'span' },
  ref
) {
  return (
    <Tag
      ref={ref}
      className="me-hover-glide-pill"
      role="presentation"
      aria-hidden="true"
    />
  )
})

// The segmented control's engine wiring. React owns the pill and the indicator
// elements, so a re-render never drops them, and the engine owns their geometry
// and the slide. selected reads the checked option from the live DOM, and the
// hook re-indicates whenever value changes or the group or an option changes
// size. onArrive marks the delayed selected label. Returns the two refs to
// render inside the group.
export function useSegmentedGlide(container, value, onArrive) {
  const pill = useRef(null)
  const indicator = useRef(null)
  const attached = useRef(null)
  const arrive = useRef(onArrive)
  arrive.current = onArrive
  useEffect(() => {
    if (!container.current || !pill.current || !indicator.current) return
    const engine = window.MEHoverGlide
    if (!engine) return
    const attachment = engine.attach(container.current, {
      itemSelector: '.me-btn-toggle',
      pill: pill.current,
      indicator: indicator.current,
      selected: host =>
        host.querySelector('[role="radio"][aria-checked="true"]'),
      onArrive: option => arrive.current && arrive.current(option),
    })
    attached.current = attachment
    // A resize, a wrap or a label change moves the selected option without a
    // new value. A new options list brings new elements, so it re-observes.
    const host = container.current
    const resize =
      window.ResizeObserver &&
      new window.ResizeObserver(() => attachment?.indicate())
    const observe = () => {
      resize.disconnect()
      resize.observe(host)
      host
        .querySelectorAll('.me-btn-toggle')
        .forEach(option => resize.observe(option))
    }
    const rebuilt = resize && new MutationObserver(observe)
    if (resize) {
      observe()
      rebuilt.observe(host, { childList: true })
    }
    return () => {
      rebuilt?.disconnect()
      resize?.disconnect()
      attachment?.destroy()
      attached.current = null
    }
  }, [container])
  // A selection change re-reads the DOM and slides the indicator.
  useLayoutEffect(() => {
    if (attached.current) attached.current.indicate()
  }, [value])
  return [pill, indicator]
}

export const SegmentIndicator = React.forwardRef(function SegmentIndicator(
  props,
  ref
) {
  return (
    <span
      ref={ref}
      className="me-glide-ind"
      role="presentation"
      aria-hidden="true"
    >
      <span className="me-glide-fill" aria-hidden="true" />
    </span>
  )
})
